import { isValidEmail } from '@blackbird/common/data-validator';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from '@martinjlowm/react-hook-form';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';
import z from 'zod';

import FactbirdLogo from '@/public/images/factbird-logo-black.svg';

const schema = z
  .object({
    email: z
      .string()
      // .email(), TODO - Reintroduce when zod fixes support for apostrophe in email address prefix
      .refine((value) => isValidEmail(value)),
    password: z.string().min(1),
  })
  .required();

const closedEye = keyframes`
  60% {
    d: path(" \
      m 627.013,49.4834 \
      c 5.1765,0  9.373,-4.1874   9.373,-9.3528 \
        0,-5.1654 -1.5155,6.6472  -9.373,6.6472 \
        -7.8575,0 -9.374,-11.8126 -9.374,-6.6472 \
         0,5.1654 4.1975,9.3528   9.374,9.3528 \
      z \
    ");
  }
`;

const useStyles = makeStyles()(() => ({
  svg: {
    '& path:first-of-type': {
      animationDelay: '2s',
      animation: `${closedEye} 0.2s 1 ease`,
    },
  },
}));

export type FormValues = z.infer<typeof schema>;

export interface Properties {
  onSubmit: (form: FormValues) => void;
  onForgotPassword: () => void;
  defaultValues?: Partial<FormValues>;
}

const resolver = zodResolver(schema);

const LoginForm: FC<Properties> = ({ onSubmit, onForgotPassword, defaultValues }) => {
  const { t } = useTranslation('auth');
  const [showPassword, setShowPassword] = useState(false);
  const [searchingUser, setSearchingUser] = useState(false);
  const { classes, theme } = useStyles();
  const logoRef = useRef<SVGElement | null>(null);
  const intervalId = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    register,
    handleSubmit,
    // getValues,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues,
    resolver,
  });

  // For use with SSO lookup to reveal that as an option
  // const validEmail = !('email' in errors) && getValues('email');

  // useEffect(() => {
  //   if (validEmail) {
  //     setSearchingUser(true);

  //     const searchTimeout = setTimeout(() => setSearchingUser(false), 10_000);

  //     return () => {
  //       setSearchingUser(false);
  //       clearTimeout(searchTimeout);
  //     };
  //   }
  // }, [validEmail]);

  useEffect(() => {
    intervalId.current = setInterval(() => {
      const svg = logoRef.current;

      if (svg) {
        if (svg.classList.contains(classes.svg)) {
          svg.classList.remove(classes.svg);
        } else {
          svg.classList.add(classes.svg);
        }
      }
    }, 5_000 + Math.random() * 5_000);

    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
  }, []);

  const toggleShowPassword = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CardMedia title="Factbird">
        <Box p={6}>
          <FactbirdLogo ref={logoRef} />
        </Box>
      </CardMedia>
      <CardHeader
        titleTypographyProps={{ align: 'center' }}
        subheaderTypographyProps={{ align: 'center' }}
        title={t('auth:welcomeBack', 'Welcome back')}
        subheader={t('auth:enterYourDetailsBelow', 'Enter your details below.')}
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              fullWidth
              error={'email' in errors}
              required
              type="email"
              id="email"
              autoComplete="username"
              label={t('auth:email', 'E-mail')}
              sx={{ paddingRight: 0 }}
              InputProps={{
                sx: { paddingRight: 0 },
                endAdornment: (
                  <InputAdornment sx={{ position: 'absolute', right: 0, margin: theme.spacing(1.5) }} position="end">
                    <IconButton disabled>
                      <CircularProgress
                        sx={{ opacity: searchingUser ? '1' : '0' }}
                        color="inherit"
                        disableShrink
                        size={'1em'}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...register('email')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              error={'password' in errors}
              required
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              label={t('auth:password', 'Password')}
              InputProps={{
                sx: { paddingRight: 0 },
                endAdornment: (
                  <InputAdornment sx={{ position: 'absolute', right: 0, margin: theme.spacing(1.5) }} position="end">
                    <IconButton tabIndex={-1} onClick={toggleShowPassword}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...register('password')}
            />
          </Grid>
        </Grid>
      </CardContent>
      <CardActions sx={{ p: 2 }}>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item xs={12}>
            <Button fullWidth disabled={!isValid} variant="contained" color="primary" type="submit">
              {t('auth:signInTitle', 'Sign In')}
            </Button>
          </Grid>
          <Grid item>
            <Button color="primary" onClick={onForgotPassword}>
              {t('auth:forgotPasswordQuestion', 'Forgot password?')}
            </Button>
          </Grid>
        </Grid>
      </CardActions>
    </form>
  );
};

export default LoginForm;
